import { useMemo, useEffect, useState, memo } from 'react';
import clsx from 'clsx';
// mantine
import { ActionIcon, ScrollArea } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
// icons
import { RefreshCcw } from 'lucide-react';
// components
import { IConversationsActor } from '@newo/api/gateway';
import ActsHistory from '@/modules/Chat/components/ActsHistory';
import ActorCombobox from '@/modules/Personas/components/ActorCombobox';
// utils
import { usePaginatedConversationsActsQuery } from '@/services/bff/bff.queries.ts';
import useViewport from '@/utils/hooks/useViewport.ts';
import useConversationStore from '@/pages/Conversations/Conversations.store.ts';
// assets
import styles from '@/pages/Conversations/components/Messages/Messages.module.css';

interface MessagesProps {
    className?: string;
    actors?: IConversationsActor[];
}

const EMPTY_ARRAY: IConversationsActor[] = [];

const DEBOUNCE_TIMEOUT = 200;

const Messages = ({ actors = EMPTY_ARRAY, className }: MessagesProps) => {
    const { viewport, ref, isIntersecting, scrollToBottom } = useViewport({
        rootMargin: '1000px 0px 0px',
        threshold: 0
    });
    const [userActorId, setUserActorId] = useState<string | undefined>();
    const actsFilters = useConversationStore((state) => state.actsFilters);
    const [debouncedActsFilters] = useDebouncedValue(
        actsFilters,
        DEBOUNCE_TIMEOUT
    );
    const selectedUserPersona = useConversationStore(
        (state) => state.selectedUserPersona
    );

    useEffect(() => {
        setUserActorId(undefined);
    }, [actors]);

    const { data, isFetching, hasNextPage, fetchNextPage, refetch } =
        usePaginatedConversationsActsQuery(
            {
                ...debouncedActsFilters,
                user_persona_id: selectedUserPersona?.id,
                user_actor_id: userActorId,
                page: 1,
                per: 50
            },
            !!selectedUserPersona?.id
        );

    const pages = data?.pages.length ?? 0;
    const acts = useMemo(
        () => (data?.pages ?? []).flatMap((page) => page.items).toReversed(),
        [data?.pages]
    );

    useEffect(() => {
        if (!isFetching && isIntersecting && hasNextPage) {
            fetchNextPage();
        }
    }, [isIntersecting, fetchNextPage, hasNextPage, isFetching]);

    useEffect(() => {
        if (acts.length > 0 && viewport.current?.scrollTop === 0) {
            viewport.current.scrollTo({
                top: 10
            });
        }
    }, [acts, scrollToBottom, viewport]);

    useEffect(() => {
        if (pages <= 1) {
            scrollToBottom();
        }
    }, [pages, scrollToBottom]);

    const handleRefetch = () => {
        refetch();
    };

    return (
        <div className={clsx(styles.content, className)}>
            <div className={styles.top}>
                <div className="text-xs font-semibold uppercase leading-none">
                    Chronicle
                </div>
                <ActionIcon
                    color="dark"
                    loading={isFetching}
                    size="xs"
                    variant="transparent"
                    onClick={handleRefetch}
                >
                    <RefreshCcw />
                </ActionIcon>
                {actors.length > 1 && (
                    <ActorCombobox
                        actors={actors}
                        className="w-52"
                        placeholder="Select Actor"
                        value={userActorId}
                        onChange={(value) => {
                            setUserActorId(value);
                        }}
                    />
                )}
            </div>
            <div className={styles.scrollAreaWrapper}>
                <ScrollArea
                    className={styles.scrollArea}
                    viewportRef={viewport}
                >
                    <div className={styles.list}>
                        <div ref={ref} />
                        <ActsHistory acts={acts} />
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
};

export default memo(Messages);
